
import React, { FunctionComponent, MouseEvent, useState } from 'react';

import Draggable from 'react-draggable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faClone, faPen, faSyncAlt, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Editor } from './Editor';
import { CodeId, FunctionId, FunctionModel } from '../utils/model';



export interface FunctionProps {
  model: FunctionModel;
  code: string;
  onDrag: (functionId: FunctionId, x: number, y: number) => void;
  onInputClick: (id: FunctionId, inputName: string) => void;
  onOutputClick: (id: FunctionId, outputName: string, e: MouseEvent) => void;
  onCodeUpdate: (functionId: FunctionId, codeId: CodeId, newCode: string, multiple: boolean) => void;
  onClone: (id: FunctionId) => void;
  onDelete: (id: FunctionId) => void;
  onExecute: (id: FunctionId) => void;
}

export const Function: FunctionComponent<FunctionProps> = ({
  model, code,
  onDrag,
  onInputClick, onOutputClick,
  onCodeUpdate,
  onClone, onDelete, onExecute
}) => {

  const [ open, setOpen ] = useState(false);

  const segments: { type: 'input' | 'empty_output' | 'taken_output', name: string, value?: any }[] = [];
  const maxLength = Math.max(model.inputs.length, model.outputs.length);
  for (let i = 0 ; i < maxLength ; i++) {
    if (!!model.inputs[i]) {
      segments.push({ type: 'input', name: model.inputs[i].name, value: model.inputs[i].value });
    }
    if (!!model.outputs[i]) {
      segments.push({ type: model.outputs[i].arrowIds.length > 0 ? 'taken_output' : 'empty_output', name: model.outputs[i].name, value: model.outputs[i].value });
    }
  }

  const handleDrag = (e: any) => {
    onDrag(model.id, e.clientX, e.clientY);
  };

  const handleInputClick = (inputName: string) => {
    onInputClick(model.id, inputName);
  };

  const handleOutputClick = (outputName:  string, e: MouseEvent) => {
    onOutputClick(model.id, outputName, e);
  };

  const handleEditClick = () => {
    setOpen(true);
  };


  const handleEditorValidate = (code: string, multiple = false) => {
    onCodeUpdate(model.id, model.codeId, code, multiple);
    setOpen(false);
  };

  const handleEditorCancel = () => {
    setOpen(false);
  };

  const handleCloneClick = () => {
    onClone(model.id);
  }

  const handleDeleteClick = () => {
    onDelete(model.id);
  }

  const handleExecuteClick = () => {
    onExecute(model.id);
  }

  return (
    <>
      {
        open ? <Editor value={code} onValidate={handleEditorValidate} onCancel={handleEditorCancel} /> : ''
      }
      <Draggable handle=".cursor-move" defaultPosition={{ x: model.x ?? 100, y: model.y ?? 200 }} onDrag={e => handleDrag(e)}>
        <div className="absolute flex flex-col bg-gray-600 rounded-md">
          <div className="cursor-move bg-yellow-400 px-4 py-1 rounded-t-md text-center">{model.name}</div>
          <div className="h-full mb-4">

            {
              segments.map(segment => {
                const segmentId = `${model.id}_${ segment.type === 'input' ? 'input' : 'output' }_${segment.name}`;
                return segment.type === 'input' ?
                  <div key={segmentId} className="flex flex-row items-center gap-2 my-2 mr-2">
                    <div id={segmentId} className="w-3 h-3 mt-1.5 rounded-full bg-gray-800 cursor-pointer hover:bg-yellow-400" onClick={() => handleInputClick(segment.name)}></div>
                    <div className="text-gray-200">{segment.name}</div>
                  </div>
                :
                  <div key={segmentId} className="flex flex-row justify-end items-center gap-2 my-2 ml-2 group">
                    <div className="text-gray-200">{segment.name}</div>
                    <div id={segmentId} className="w-3 h-3 mt-1.5 rounded-full bg-gray-800 cursor-pointer hover:bg-yellow-400" onClick={e => handleOutputClick(segment.name, e)}></div>
                      <div className={`absolute left-full w-max bg-gray-800 text-gray-200 z-0 py-1 pr-2 rounded-r-md transform translate-y-0.5 ${segment.type === 'empty_output' ? '': 'invisible group-hover:visible'}`}>
                        <FontAwesomeIcon icon={faArrowRight} size="sm" className="mr-2 text-yellow-400" />
                        <span>{JSON.stringify(segment.value ?? 'none')}</span>
                      </div>
                  </div>
                    
                ;
              })
            }

          </div>
          <div className="bg-gray-200 rounded-b-md flex flex-row justify-center gap-2 py-1">
            <button onClick={handleEditClick} className="text-gray-800 hover:bg-gray-400 rounded-full px-1.5">
              <FontAwesomeIcon icon={faPen} size="xs" />
            </button>
            <button onClick={handleCloneClick} className="text-gray-800 hover:bg-gray-400 rounded-full px-1.5">
              <FontAwesomeIcon icon={faClone} size="xs" />
            </button>
            <button onClick={handleDeleteClick} className="text-gray-800 hover:bg-gray-400 rounded-full px-1.5">
              <FontAwesomeIcon icon={faTrash} size="xs" />
            </button>
            <button onClick={handleExecuteClick} className="text-gray-800 hover:bg-gray-400 rounded-full px-1.5">
              <FontAwesomeIcon icon={faSyncAlt} size="xs" />
            </button>
          </div>
        </div>
      </Draggable>
    </>
  );
};
